import { useApiQuery } from '@/api/main/query'
import { Roles } from '@/entities/roles'
import { TopupModel, TopupModelMerchant } from '@/entities/topup'
import { topupsAPI } from '@/entities/topups'
import { USER_ROLE_KEY } from '@/features/auth'
import { useGetDeviceData } from '@/features/devices/hooks/useGetDeviceData'
import { useDisputModals } from '@/features/orders/hooks/useDisputModals'
import { useGetDisputData } from '@/features/orders/hooks/useGetDisputData'
import { DisputStatus } from '@/features/orders/model/types'
import { AcceptedOrder } from '@/features/orders/ui/dialogs/AcceptedOrder'
import { AcceptOrderDialog } from '@/features/orders/ui/dialogs/AcceptOrderDialog'
import { CanceledOrder } from '@/features/orders/ui/dialogs/CanceledOrder'
import { CancelOrderDialog } from '@/features/orders/ui/dialogs/CancelOrderDialog'
import { OpenOrderDialog } from '@/features/orders/ui/dialogs/OpenOrderDialog'
import { MobileOrderBanner } from '@/features/orders/ui/mobile/MobileOrderBanner/MobileOrderBanner'
import { OrderBanner } from '@/features/orders/ui/Order/OrderBanner/OrderBanner'
import { OrderHeader } from '@/features/orders/ui/Order/OrderHeader/OrderHeader'
import { RequisiteBlock } from '@/features/requisites'
import { BaseCard } from '@/shared/components'
import { DealBlock } from '@/shared/components/DealBlock/DealBlock'
import { DeviceBlock } from '@/shared/components/DeviceBlock/DeviceBlock'
import { BasePageSkeleton } from '@/shared/components/Skeletones/BasePageSkeleton'
import { BlockSkeleton } from '@/shared/components/Skeletones/BlockSkeleton'
import { formatCurrency } from '@/shared/lib/currency'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import { useIsMobile } from '@/shared/utils'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

export const Order = () => {
  const isMobile = useIsMobile()
  const { id } = useParams<{ id: string }>()
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)
  const isTrader = userRole === Roles.Trader

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const {
    disputData,
    isLoading: isDisputLoad,
    error: disputError,
  } = useGetDisputData({ disputId: id || '' })

  const { confirmedAmount, modalSetters, modalStates, handlers } = useDisputModals()

  const {
    data: queryTopupData,
    error: topupError,
    isLoading: topupIsLoading,
  } = useApiQuery<TopupModel | TopupModelMerchant>(
    ['topup', disputData.paymentId],
    topupsAPI.item(disputData.paymentId),
    undefined,
    { enabled: Boolean(disputData.paymentId) && disputData.paymentId !== 'Нет данных' }
  )

  const { deviceData, isLoading: isDeviceLoading } = useGetDeviceData({
    deviceId: isTrader ? ((queryTopupData as TopupModel)?.deviceProfile.id ?? '') : '',
  })

  const topupAmount = formatCurrency(
    queryTopupData?.amountFrom || 0,
    queryTopupData?.currencyFrom || 0
  )

  const openDialogHandler =
    disputData.status === DisputStatus.Created || disputData.status === DisputStatus.Reopened
      ? handlers.openDisputDialog
      : handlers.inProgressDisputDialog

  return disputError ? (
    <p>Ошибка загрузки данных.</p>
  ) : isDisputLoad ? (
    <BasePageSkeleton />
  ) : (
    <>
      <div className="flex flex-col gap-6 mb-[30px] md:mb-[40px]">
        <OrderHeader
          disput={disputData}
          openDisputDialog={openDialogHandler}
          topupAmount={topupAmount}
        />
        {isMobile ? (
          <MobileOrderBanner
            disput={disputData}
            deviceName={deviceData.name}
            topup={queryTopupData}
            topupError={topupError}
            topupIsLoad={topupIsLoading}
            openDisputDialog={openDialogHandler}
          />
        ) : (
          <OrderBanner disput={disputData} />
        )}
      </div>
      <div className="flex flex-col gap-[30px]">
        {topupError ? (
          <BaseCard variant="primary" className="flex flex-col gap-6 px-6 py-8">
            <h3>Сделка</h3>
            <p>Ошибка загрузки данных.</p>
          </BaseCard>
        ) : topupIsLoading || !queryTopupData ? (
          <>
            <BlockSkeleton />
            <BlockSkeleton />
            <BlockSkeleton />
          </>
        ) : (
          <>
            <DealBlock
              data={queryTopupData}
              deviceName={isTrader ? deviceData.name : undefined}
              userRole={userRole}
            />
            <RequisiteBlock data={queryTopupData} userRole={userRole} />
            {userRole === Roles.Trader && (
              <DeviceBlock data={deviceData} isLoading={isDeviceLoading} />
            )}
          </>
        )}

        {/* Dialogs */}
        {userRole === Roles.Trader && (
          <>
            <OpenOrderDialog
              disputId={disputData.id}
              isModalOpen={modalStates.isDisputActionOpen}
              setIsModalOpen={modalSetters.setIsDisputActionOpen}
              onSubmit={handlers.acceptDispute}
              onCancel={handlers.rejectDisput}
            />

            <AcceptOrderDialog
              amount={confirmedAmount}
              isModalOpen={modalStates.isConfirmAmountOpen}
              setIsModalOpen={modalSetters.setIsConfirmAmountOpen}
              onAction={handlers.confirmAmount}
            />
            <AcceptedOrder
              isModalOpen={modalStates.isAcceptResultOpen}
              onConfirm={handlers.closeAcceptResult}
            />

            <CancelOrderDialog
              isModalOpen={modalStates.isRejectConfirmOpen}
              onClose={modalSetters.setIsRejectConfirmOpen}
              onSubmit={handlers.closeRejectDialog}
            />
            <CanceledOrder
              isModalOpen={modalStates.isRejectResultOpen}
              onConfirm={handlers.closeRejectResult}
            />
          </>
        )}
      </div>
    </>
  )
}
